/**
 * Shared cart state.
 *
 * Every component that wants to know what is in the cart used to fetch
 * /cart.js for itself. One add-to-cart click fired six cart requests: the
 * add, the sidebar refresh, two delivery-guarantee syncs, the cart-view
 * analytics event and the standard-actions refresh. They queued behind each
 * other on the same connection and the sidebar could not open until its own
 * one landed.
 *
 * This module is the single owner of that state:
 *
 *   - seeded from the Liquid snapshot so the first paint needs no network,
 *   - coalesced so concurrent callers share one in-flight request,
 *   - mergeLine() folds a /cart/add response into the known cart so the
 *     sidebar can render exact line prices before any reconcile lands.
 *
 * Totals derived by mergeLine are exact unless the cart carries an
 * order-level discount, which only Shopify can price. In that case the
 * derived cart is flagged `estimated` so the UI can hold the total back
 * until the reconcile arrives.
 */
window.H7CartState =
  window.H7CartState ||
  (() => {
    const now = () => (window.performance?.now ? performance.now() : Date.now());
    const cartUrl = () => `${(typeof routes !== 'undefined' && routes?.cart_url) || '/cart'}.js`;

    let snapshot = null;
    let snapshotAt = 0;
    let inFlight = null;
    let revision = 0;
    let inFlightRevision = 0;
    let dirty = false;
    let seeded = false;
    // True while everything we know traces back to the Liquid snapshot. That
    // snapshot is only as fresh as the page render, so a cart changed in
    // another tab would be missing lines. Callers use this to decide whether a
    // background reconcile is still owed.
    let fromSeed = false;
    const listeners = new Set();

    const seed = () => {
      if (seeded) return;
      seeded = true;
      const node = document.querySelector('[data-h7-cart-snapshot]');
      if (!node) return;
      try {
        const parsed = JSON.parse(node.textContent);
        // Require the two fields every consumer depends on. Anything else and
        // we stay unseeded, which falls the whole theme back to fetching.
        if (parsed && Array.isArray(parsed.items) && parsed.currency) {
          snapshot = parsed;
          snapshotAt = now();
          fromSeed = true;
        }
      } catch (error) {
        // A malformed snapshot must never break the cart; we just fetch instead.
      }
    };

    const notify = () => {
      listeners.forEach((listener) => {
        try {
          listener(snapshot);
        } catch (error) {
          // A broken listener must not stop the others.
        }
      });
    };

    const set = (cart) => {
      if (!cart || !Array.isArray(cart.items)) return snapshot;
      revision += 1;
      dirty = false;
      snapshot = cart;
      snapshotAt = now();
      // Only a cart that came from Shopify confirms freshness. Our own derived
      // cart comes back around through the cart-update event, and treating
      // that as confirmation would cancel the reconcile it still owes.
      if (!cart.derived) fromSeed = false;
      notify();
      return snapshot;
    };

    // Order-level discounts are priced by Shopify, not by us, so a locally
    // derived total cannot be trusted when one is applied.
    const hasOrderDiscount = (cart) =>
      Boolean(cart?.cart_level_discount_applications?.length) ||
      Boolean(cart?.discount_applications?.some((discount) => discount.target_type === 'shipping_line'));

    const derive = (base, items) => {
      const finalTotal = items.reduce((total, item) => total + Number(item.final_line_price || 0), 0);
      const originalTotal = items.reduce(
        (total, item) => total + Number(item.original_line_price || item.final_line_price || 0),
        0
      );

      return {
        ...base,
        items,
        item_count: items.reduce((count, item) => count + (Number(item.quantity) || 0), 0),
        items_subtotal_price: finalTotal,
        total_price: finalTotal,
        original_total_price: originalTotal,
        total_discount: Math.max(0, originalTotal - finalTotal),
        estimated: hasOrderDiscount(base),
        derived: true,
      };
    };

    /**
     * Fold a /cart/add response into the known cart. The response is the
     * resulting line, carrying the line's new total quantity and prices, so
     * replacing by key is idempotent: merging the same response twice gives
     * the same cart.
     */
    const mergeLine = (line) => {
      seed();
      if (!line?.key || !snapshot) return null;

      const items = [...snapshot.items];
      const index = items.findIndex((item) => item.key === line.key);
      if (index > -1) items[index] = { ...items[index], ...line };
      else items.push(line);

      const merged = derive(snapshot, items);
      revision += 1;
      snapshot = merged;
      snapshotAt = now();
      notify();
      return merged;
    };

    /**
     * @param {{maxAge?: number}} options maxAge in ms; state younger than
     *   that is returned without a request. Omit for a guaranteed round trip.
     */
    const get = ({ maxAge = 0 } = {}) => {
      seed();

      if (snapshot && maxAge > 0 && now() - snapshotAt < maxAge && !dirty) {
        return Promise.resolve(snapshot);
      }
      // Only share a request that started after the last known mutation,
      // otherwise callers would be handed pre-mutation state.
      if (inFlight && inFlightRevision === revision) return inFlight;

      const requestRevision = revision;
      inFlightRevision = requestRevision;
      // Timeboxed: the in-flight promise is shared with every later caller,
      // so one hung request would otherwise pin them all until the browser
      // gives up on the socket.
      const pending = fetch(cartUrl(), {
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout?.(12000),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          return response.json();
        })
        .then((cart) => {
          if (requestRevision !== revision) {
            if (inFlight && inFlight !== pending) return inFlight;
            return dirty ? get() : snapshot;
          }
          if (!cart || !Array.isArray(cart.items) || !cart.currency) {
            throw new Error('Invalid cart response');
          }
          return set(cart);
        })
        .finally(() => {
          if (inFlight === pending) inFlight = null;
        });

      inFlight = pending;
      return pending;
    };

    const peek = () => {
      seed();
      return snapshot;
    };

    const invalidate = () => {
      revision += 1;
      dirty = true;
    };

    /**
     * True when the cart still has to be confirmed against Shopify. The
     * added line itself is always authoritative (the add response carries its
     * new quantity and prices), so this only ever concerns the other lines.
     */
    const needsReconcile = () => Boolean(fromSeed) || dirty || !snapshot;

    const subscribe = (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    };

    return { get, peek, set, mergeLine, invalidate, needsReconcile, subscribe };
  })();
